import {
  isTaskOptional,
  shouldStepAppear,
  shouldTaskAppear,
  type Group,
  type GroupComputed,
  type GroupStatus,
  type Question,
  type QuestionComputed,
  type Step,
  type StepComputed,
  type Task,
  type TaskComputed,
} from "~onboarding/utils";
import type { SubAttribute } from "~utils";

/********** Types **********/
/**
 * Represents a local change in the shape of a Sub Attribute where only the
 * `attribute` field is required, while all others are optional.
 *
 * NOTE:
 * - `value` field is optional, and omission of this field means that the value
 * has been removed.
 */
export type UnsyncedSubAttribute = Pick<SubAttribute, "attribute"> &
  Partial<Omit<SubAttribute, "attribute">>;

/********** Group Computations **********/
/** Given static Groups and attributes, return the computed Groups */
export function computeGroupsState(params: {
  groups: Group[];
  syncedSubAttributes?: Array<SubAttribute>;
}): GroupComputed[] {
  const { groups, syncedSubAttributes = [] } = params;

  // Global queue for incomplete optional tasks
  let incompleteOptionalTasks: TaskComputed[] = [];

  // For each Group
  const computedGroups = groups.reduce<GroupComputed[]>((groupComputed, group) => {
    // Compute each Task in the Group (e.g. hydrate it's status, progress, etc.)
    const tasks = group.tasks
      .map((task) => computeTaskState({ task, syncedSubAttributes }))
      // Filter out Tasks that don't match their appearWhen criteria
      .filter((task) => task !== null);

    // Next determine the current Groups status based on the previous Group's status
    const previousGroupStatus = groupComputed.at(-1)?.status;

    // If the previous Group is in a `IN_PROGRESS` or `BLOCKED` state
    if (previousGroupStatus === "IN_PROGRESS" || previousGroupStatus === "BLOCKED") {
      // Then the current Group should be `BLOCKED`
      return [
        ...groupComputed,
        {
          tasks,
          status: "BLOCKED" as GroupStatus,
        },
      ];
    }

    // If the previous Group is in a `COMPLETED` state (we know via the if
    // statement above)
    // Then we need to check if the current Group status (either `IN_PROGRESS`
    // or `COMPLETED`)

    // If NOT all the required Tasks are completed
    if (
      tasks.filter((task) => !isTaskOptional(task)).every((task) => task.status === "COMPLETED") ===
      false
    ) {
      // Then the current Group is `IN_PROGRESS`
      // And we need to add all the queued incomplete optional tasks to the
      // end of the current Group's Task list.
      const queuedTasks = [...incompleteOptionalTasks];
      incompleteOptionalTasks = []; // Clear the queue

      return [
        ...groupComputed,
        {
          tasks: [...tasks, ...queuedTasks],
          status: "IN_PROGRESS" as GroupStatus,
        },
      ];
    }

    // Lastly, if the current Group status is `COMPLETED` (we know via the if
    // statement above)
    // Then store the incomplete optional Tasks for the next Group (unless
    // there are no more Groups)
    // And we need to add all the completed optional Tasks to the end of the
    // current Group's Task list.
    const currentGroupIncompleteOptionalTasks = tasks.filter(
      (task) => isTaskOptional(task) && task.status !== "COMPLETED",
    );
    incompleteOptionalTasks.push(...currentGroupIncompleteOptionalTasks);

    return [
      ...groupComputed,
      {
        tasks: tasks.filter((task) => task.status === "COMPLETED"), // Only completed tasks stay
        status: "COMPLETED" as GroupStatus,
      },
    ];
  }, [] as GroupComputed[]);

  // Lastly, if there are any remaining incomplete optional tasks
  if (incompleteOptionalTasks.length > 0) {
    // Then add them to the last Group
    // TODO: This may change in the future as we allow users complete Optional
    // tasks from the onboarding flow.
    computedGroups.at(-1)?.tasks.push(...incompleteOptionalTasks);
  }

  return computedGroups;
}

/********** Task Computations **********/
export function computeTaskState(params: {
  task: Task;
  syncedSubAttributes?: Array<SubAttribute>;
  unsyncedSubAttributes?: Array<UnsyncedSubAttribute>;
}): TaskComputed | null {
  const { task, syncedSubAttributes = [], unsyncedSubAttributes = [] } = params;

  // Check if task should appear based on appearWhen criteria
  if (
    !shouldTaskAppear({
      appearsWhen: task.appearsWhen,
      syncedSubAttributes,
      unsyncedSubAttributes,
    })
  ) {
    return null;
  }

  const steps = task.steps
    .map((step) => computeStepState({ step, syncedSubAttributes, unsyncedSubAttributes }))
    // Filtering is required since Steps which are no longer valid due to
    // branching will be returned as `null`.
    .filter((step) => step !== null);

  // Compute the remaining fields based on the filtered Steps
  const status = computeStepOrTaskStatus(steps);
  const progress = computeTaskProgress({ steps, status });
  const updatedAt = computeStepOrTaskUpdatedAt(steps);

  return { ...task, steps, status, progress, updatedAt };
}

/**
 * Returns the Task progress based on the answered questions / total questions.
 *
 * NOTE: We are using answered question instead of completed questions since
 * this progress calculation is performed with unsynced changes as well.
 */
export function computeTaskProgress(params: {
  steps: Array<StepComputed>;
  status?: TaskComputed["status"];
}): number {
  const { steps, status } = params;

  // If the status is `UNDER_REVIEW` then the progress should be 1 (100%)
  if (status === "UNDER_REVIEW") return 1;

  // Otherwise, the progress is the answered questions / total questions
  const questions = steps.flatMap((step) => step.questions);
  const totalQuestions = questions.length;
  const answeredQuestions = questions.filter((question) => question.value !== undefined).length;

  return answeredQuestions / totalQuestions;
}

/********** Step Computations **********/
/** Returns a StepComputed type */
export function computeStepState(params: {
  step: Step;
  syncedSubAttributes?: Array<SubAttribute>;
  unsyncedSubAttributes?: Array<UnsyncedSubAttribute>;
}): StepComputed | null {
  const { step, syncedSubAttributes = [], unsyncedSubAttributes = [] } = params;

  // Check if step should appear based on appearWhen criteria
  if (
    !shouldStepAppear({
      appearsWhen: step.appearsWhen,
      syncedSubAttributes,
      unsyncedSubAttributes,
    })
  ) {
    return null;
  }

  const questions = step.questions.map((question) =>
    computeQuestionState({
      question,
      syncedSubAttribute: syncedSubAttributes.find((ssa) => ssa.attribute === question.attribute),
      unsyncedSubAttribute: unsyncedSubAttributes.find(
        (usa) => usa.attribute === question.attribute,
      ),
    }),
  );

  // Compute the remaining fields based on the computed Questions
  const status = computeStepOrTaskStatus(questions);
  const updatedAt = computeStepOrTaskUpdatedAt(questions);

  return { ...step, questions, status, updatedAt };
}

/** Computes the status of the a Step or Task */
export function computeStepOrTaskStatus<TItem extends QuestionComputed | StepComputed>(
  items: Array<TItem>,
): TItem["status"] {
  const incompleteItems = items.filter((item) => item.status !== "COMPLETED");

  // When all Questions or Steps are `COMPLETED`, then the Step or Task is
  // `COMPLETED`
  if (incompleteItems.length === 0) return "COMPLETED";
  // Otherwise, return the first Question or Step's status which can either be
  // `undefined`, `UNDER_REVIEW`, or `ACTION_REQUIRED`.
  return incompleteItems[0].status;
}

/** Computes the `updatedAt` field for the computed Step or Task */
export function computeStepOrTaskUpdatedAt<TItem extends QuestionComputed | StepComputed>(
  items: Array<TItem>,
): TItem["updatedAt"] {
  const answeredItems = items.filter((item) => item.status !== undefined);

  // Bail when there are no answered items
  if (answeredItems.length === 0) return undefined;

  // Sort the `updatedAt` values in DESC order and return the last value
  const sortedUpdatedAts = answeredItems
    .filter((item) => item.updatedAt !== undefined)
    .map<Date>((item) => item.updatedAt!)
    .sort((a, b) => (a.toISOString() > b.toISOString() ? -1 : 1));

  return sortedUpdatedAts.at(0);
}

/********** Question Computations **********/
/**
 * Given a static definition of a Question and maybe Sub Attributes, return
 * the computed version of the Question.
 *
 * The one rule that this follows, is:
 * - If a Question is in a `ACTION_REQUIRED`, strip the value as it is not valid. This helps the UI
 *   reset.
 */
export function computeQuestionState(params: {
  question: Question;
  syncedSubAttribute?: SubAttribute;
  unsyncedSubAttribute?: UnsyncedSubAttribute;
}): QuestionComputed {
  const { question, syncedSubAttribute, unsyncedSubAttribute } = params;

  /** Map computed question fields between synced or unsynced sub attribute **/
  const status = syncedSubAttribute?.status ?? unsyncedSubAttribute?.status;

  // Ignoring the value when the synced sub attribute status is
  // `ACTION_REQUIRED` since the API has deemed this value to not be valid and
  // we don't want to show the invalid value to the user.
  const value =
    syncedSubAttribute?.status === "ACTION_REQUIRED"
      ? undefined
      : (syncedSubAttribute?.value ?? unsyncedSubAttribute?.value);

  const actionRequiredMarkdown =
    syncedSubAttribute?.actionRequiredMarkdown ?? unsyncedSubAttribute?.actionRequiredMarkdown;

  const updatedAt = syncedSubAttribute?.updatedAt ?? unsyncedSubAttribute?.updatedAt;

  return {
    ...question,
    status,
    value,
    actionRequiredMarkdown,
    updatedAt,
  };
}
